import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { type FormDataConvertible } from '@inertiajs/core'
import { UserRoundKeyIcon } from 'lucide-react'
import AuthLayout from '@/layouts/AuthLayout'
import { AuthCard } from '@/components/AuthCard'
import { FormField } from '@/components/FormField'
import { ErrorBanner } from '@/components/ErrorBanner'
import { base64urlToArrayBuffer, arrayBufferToBase64url } from '@/lib/webauthn'
import type { CreationOptions } from '@/types/auth'

interface Props {
  creation_options: CreationOptions
  skip_path?: string
}

type State = 'idle' | 'processing' | 'error'

export default function PasskeyNew ({ creation_options: creationOptions, skip_path: skipPath }: Props): React.ReactElement {
  const [name, setName] = useState('このデバイスのパスキー')
  const [state, setState] = useState<State>('idle')
  const [error, setError] = useState<string | null>(null)

  async function startRegistration (): Promise<void> {
    setState('processing')
    setError(null)

    try {
      const publicKey = prepareCreateOptions(creationOptions)
      const credential = await navigator.credentials.create({ publicKey })
      if (credential == null) throw new Error('パスキーの取得に失敗しました')

      router.post(
        '/auth/passkeys',
        {
          credential: serializeAttestationCredential(
            credential as PublicKeyCredential
          ) as FormDataConvertible,
          name
        },
        { onError: () => setState('error') }
      )
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        setState('idle')
      } else {
        setError(e instanceof Error ? e.message : 'パスキーの登録に失敗しました')
        setState('error')
      }
    }
  }

  function handleSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    void startRegistration()
  }

  return (
    <AuthLayout>
      <AuthCard maxWidth='max-w-[420px]'>
        <div className='flex items-center gap-[10px] mb-7'>
          <div className='w-9 h-9 bg-(--bp-accent) rounded-lg flex items-center justify-center text-[13px] font-extrabold text-white tracking-[-0.05em]'>
            BP
          </div>
          <div className='text-[18px] font-bold tracking-[-0.04em] text-(--bp-accent)'>Blueprint</div>
        </div>

        <div className='text-[22px] font-bold tracking-[-0.04em] text-(--bp-ink) mb-1.5'>パスキーを登録する</div>
        <div className='text-[13px] text-(--bp-ink3) mb-7 leading-[1.55]'>
          このデバイスで生体認証やPINを使った
          <br />
          すばやいログインが可能になります
        </div>

        <div className='bg-(--bp-accent-pale) border border-[rgba(53,71,193,0.15)] rounded-lg px-4 py-[14px] mb-5 text-xs text-(--bp-ink2) leading-[1.7]'>
          <div className='font-semibold text-(--bp-accent) mb-1'>パスキーとは？</div>
          パスワードの代わりに、このデバイスの指紋認証・顔認証・PINでログインできる仕組みです。
          フィッシング詐欺に強く、パスワードを覚える必要がありません。
        </div>

        {(error != null && error !== '') && <ErrorBanner message={error} />}

        <form onSubmit={handleSubmit}>
          <FormField
            label='デバイス名'
            hint='複数のデバイスを登録する場合に区別するための名前です'
          >
            <input
              className='w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
          <button
            className='flex items-center justify-center gap-[10px] w-full py-[13px] px-5 bg-(--bp-ink) text-white text-sm font-semibold border-none rounded-lg cursor-pointer transition-colors hover:bg-[#0F1826] disabled:opacity-50'
            type='submit'
            disabled={state === 'processing'}
          >
            <UserRoundKeyIcon width={20} height={20} />
            {state === 'processing' ? '登録中...' : 'パスキーを登録する'}
          </button>
        </form>

        {(skipPath != null && skipPath !== '') && (
          <div className='text-center mt-4'>
            <Link href={skipPath} className='text-xs text-(--bp-ink3) no-underline hover:text-(--bp-ink2)'>
              後で設定する →
            </Link>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  )
}

function prepareCreateOptions (options: CreationOptions): PublicKeyCredentialCreationOptions {
  return {
    challenge: base64urlToArrayBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64urlToArrayBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName ?? options.user.name
    },
    pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
      type: p.type as PublicKeyCredentialType,
      alg: p.alg
    })),
    timeout: options.timeout,
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      id: base64urlToArrayBuffer(c.id),
      type: c.type as PublicKeyCredentialType
    })),
    authenticatorSelection:
      options.authenticatorSelection as AuthenticatorSelectionCriteria | undefined
  }
}

function serializeAttestationCredential (credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: arrayBufferToBase64url(response.attestationObject),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON)
    }
  }
}
